import {useEffect, useRef} from "react";
import Konva from "konva";
import {MAX_SCALE} from "../../../project-drawings/features/editor/constants";
import {ZoomState} from "../../types";

const DOUBLE_TAP_MAX_MS = 300
const DOUBLE_TAP_MAX_PX = 30

type UseDoubleTapProps = {
	zoomState: ZoomState
	setZoomState: (zoomState: ZoomState) => void
	stageRef: React.RefObject<Konva.Stage>
	handleZoomFitToScreen: () => void
}

export function useDoubleTap({zoomState, setZoomState, stageRef, handleZoomFitToScreen}: UseDoubleTapProps) {
	const lastTapTime = useRef<number>(0)
	const lastTapX = useRef<number>(0)
	const lastTapY = useRef<number>(0)
	const zoomStateRef = useRef(zoomState)

	useEffect(() => {
		zoomStateRef.current = zoomState
	}, [zoomState])

	const onTouchEnd = (event: Konva.KonvaEventObject<TouchEvent>) => {
		const now = Date.now()
		const evt = event.evt

		if (evt.touches.length > 0) return

		const touch = evt.changedTouches.item(0)
		if (!touch) return

		const tapX = touch.clientX
		const tapY = touch.clientY
		const elapsed = now - lastTapTime.current
		const distFromLast = Math.hypot(tapX - lastTapX.current, tapY - lastTapY.current)

		if (elapsed < DOUBLE_TAP_MAX_MS && distFromLast < DOUBLE_TAP_MAX_PX) {
			// Double-tap detected
			const currentZoom = zoomStateRef.current
			const nearMax = Math.round(currentZoom.scale * 100) >= MAX_SCALE * 100
			if (nearMax) {
				handleZoomFitToScreen()
			} else {
				const stage = stageRef.current
				if (!stage) return

				const newScale = Math.min(currentZoom.scale * 2, MAX_SCALE)

				// Zoom centred on tap point
				const stageRect = stage.container().getBoundingClientRect()
				const pointerX = tapX - stageRect.left
				const pointerY = tapY - stageRect.top
				const oldScale = currentZoom.scale
				const pointOnStageX = (pointerX - stage.x()) / oldScale
				const pointOnStageY = (pointerY - stage.y()) / oldScale
				stage.position({
					x: pointerX - pointOnStageX * newScale,
					y: pointerY - pointOnStageY * newScale,
				})
				stage.batchDraw()
				setZoomState({...currentZoom, scale: newScale})
			}

			// Reset so a third tap isn't another double-tap
			lastTapTime.current = 0
			lastTapX.current = 0
			lastTapY.current = 0
		} else {
			lastTapTime.current = now
			lastTapX.current = tapX
			lastTapY.current = tapY
		}
	}

	return {onTouchEnd}
}
